import React, { useContext } from "react";
import PropTypes from "prop-types";
import qlog from "views_shared/app/javascript/scripts/qlog";
import qapi from "views_shared/app/javascript/api";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { CompanyContext } from "../data/company_context_management";

function ExpertForm({
  companyHidden,
  companyHighlighted,
  companyId,
  companyJobId,
  endedAt = null,
  personProfileImageUrl,
  personName,
  title = undefined,
  setDoExpertsFetch,
}) {
  // A past job (ended_at set) still lists here — this modal is where the company configures it —
  // but the public page hides it unless the company highlights it. The badge and dimming tell the
  // owner why someone in this list is absent from the page.
  const past = !!endedAt;
  const { setDoTopLevelFetch } = useContext(CompanyContext);
  const handleExpertDisplayChange = async (e) => {
    const target = e.target.id;
    let toggledHideValue;
    let toggledHighlightedValue;

    if (target.includes("hide")) {
      toggledHideValue = !companyHidden;
      // Turn off Highlighted if Hide is turned on
      toggledHighlightedValue = toggledHideValue ? false : companyHighlighted;
    } else if (target.includes("highlighted")) {
      // Turn off Hide if Highlighted is turned on
      toggledHighlightedValue = !companyHighlighted;
      toggledHideValue = toggledHighlightedValue ? false : companyHidden;
    } else {
      toggledHideValue = companyHidden;
      toggledHighlightedValue = companyHighlighted;
    }

    const params = {
      company_hidden: toggledHideValue,
      company_highlighted: toggledHighlightedValue,
    };

    try {
      const response = await qapi.companyJobs.updateByCompanyIdAndId(companyId, companyJobId, params);

      if (response.error) {
        throw new Error(JSON.stringify(response.error));
      } else {
        setDoExpertsFetch(true);
        setDoTopLevelFetch(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert("Sorry, something went wrong. Please try again.");
      qlog.warn("Error when attempting to update Expert: ", err);
    }
  };

  return (
    <div className="d-flex mb-3">
      <div>
        <ImageCropped
          roundedCircle
          wrapperClasses={past ? "me-2 opacity-75" : "me-2"}
          src={personProfileImageUrl}
          alt={personName}
          size={60}
          fallbackToInitials
        />
      </div>

      <div>
        <div className="fw-bold font-size-14px">
          <span className={past ? "opacity-75" : undefined}>{personName}</span>
          {past && <span className="badge rounded-pill text-bg-secondary fw-normal ms-2">Past</span>}
        </div>

        {title && (
          <div className={past ? "text-secondary font-size-14px opacity-75" : "text-secondary font-size-14px"}>
            {title}
          </div>
        )}

        <div className="d-flex">
          <div className="form-check form-switch me-3">
            <label className="form-check-label" htmlFor={`hide-${companyJobId}`}>
              Hide
              <input
                aria-label="Hide"
                id={`hide-${companyJobId}`}
                type="checkbox"
                className="form-check-input"
                checked={companyHidden}
                onChange={(e) => handleExpertDisplayChange(e)}
              />
            </label>
          </div>

          <div className="form-check form-switch">
            <label className="form-check-label" htmlFor={`highlighted-${companyJobId}`}>
              Highlight
              <input
                id={`highlighted-${companyJobId}`}
                type="checkbox"
                className="form-check-input"
                checked={companyHighlighted}
                onChange={(e) => handleExpertDisplayChange(e)}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

ExpertForm.propTypes = {
  companyHidden: PropTypes.bool.isRequired,
  companyHighlighted: PropTypes.bool.isRequired,
  companyId: PropTypes.number.isRequired,
  companyJobId: PropTypes.number.isRequired,
  endedAt: PropTypes.string,
  personName: PropTypes.string.isRequired,
  personProfileImageUrl: PropTypes.string.isRequired,
  setDoExpertsFetch: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default ExpertForm;
